import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {
  EmButtonModule,
  EmIconButtonComponent,
  EmIconModule,
  EmInputModule,
  EmSelectModule,
} from '@em/shared/ui';
import {TranslateModule} from '@ngx-translate/core';
import {EmAdditionalProcessorComponent} from '../additional-processors/additional-processor/additional-processor.component';
import {EmCalculationMappingComponent} from '../calculation-mapping/calculation-mapping.component';
import {EmCombineAttributesModule} from '../combine-attributes/combine-attributes.modul';
import {EmConditionComponent} from '../condition/condition.component';
import {EmConditionalStatementListComponent} from '../conditional-statement-list/conditional-statement-list.component';
import {EmOperandIconModule} from '../operand-icon/operand-icon.module';
import {EmSelectOperandModule} from '../select-operand/select-opearnd.module';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  FormControl,
  FormGroup,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  AdditionalProcessorFormGroup,
  MappingRuleType,
  OperatorGroups,
} from '@em/shared/mapping-engine/domain-models';
import {expandCollapseAnimation} from '@em/shared/ui';
import {MappingEngineFormBuilderService} from '../../services/mapping-engine-form-builder/mapping-engine-form-builder.service';

interface MappingOperator {
  type: MappingRuleType;
  title: string;
  icon: string;
}
const AvailableMappingTypes: MappingOperator[] = [
  {title: 'SHARED_LABEL_ATTRIBUTE', type: 'attribute', icon: 'shopping_bag'},
  {
    title: 'SHARED_LABEL_STATIC_VALUE',
    type: 'static_value',
    icon: 'text_fields',
  },
  {
    title: 'MAPPING_ENGINE_MAPPING_COMBINE_ATTRIBUTES',
    type: 'combine_attributes',
    icon: 'merge',
  },
  {
    title: 'MAPPING_ENGINE_MAPPING_CALCULATION',
    type: 'calculation',
    icon: 'calculate',
  },
  {
    title: 'MAPPING_ENGINE_MAPPING_TYPE_EMPTY',
    type: 'leave_empty',
    icon: 'hide_source',
  },
];

@Component({
  selector: 'em-mapping-rule',
  templateUrl: './mapping-rule.component.html',
  styleUrls: ['./mapping-rule.component.scss'],
  animations: [expandCollapseAnimation],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatIconModule,
    MatMenuModule,
    EmIconModule,
    EmSelectModule,
    EmInputModule,
    EmButtonModule,
    EmIconButtonComponent,
    EmSelectOperandModule,
    EmConditionalStatementListComponent,
    EmCombineAttributesModule,
    EmCalculationMappingComponent,
    EmOperandIconModule,
    EmAdditionalProcessorComponent,
    EmConditionComponent,
  ],
})
export class EmMappingRuleComponent implements OnInit {
  private readonly _controlContainer = inject(ControlContainer);
  private readonly _fb = inject(MappingEngineFormBuilderService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _cd = inject(ChangeDetectorRef);

  @Input() availableAttributes: string[] | null | undefined;
  @Input() customAttributes: string[] | null | undefined;
  @Input() operatorGroups: OperatorGroups | undefined;
  @Input() allowDelete = false;
  @Input() allowCondition = true;
  @Input() allowClearAllConditions = false;
  @Output() delete: EventEmitter<boolean> = new EventEmitter<boolean>();

  readonly AvailableMappingTypes = AvailableMappingTypes;
  mappingRuleFormGroup: UntypedFormGroup | null | undefined;

  //#region  Properties
  get conditionalStatementListControl(): FormGroup {
    return this.mappingRuleFormGroup?.get(
      'conditionalStatementList',
    ) as FormGroup;
  }
  get mappingTypeControl(): UntypedFormControl {
    return this.mappingRuleFormGroup?.get('mappingType') as UntypedFormControl;
  }
  get mappingValueControl(): UntypedFormControl {
    return this.mappingRuleFormGroup?.get('mappingValue') as UntypedFormControl;
  }
  get additionalProcessorFormGroup(): AdditionalProcessorFormGroup {
    return this.mappingRuleFormGroup?.get(
      'additionalProcessor',
    ) as AdditionalProcessorFormGroup;
  }
  get slectedType(): MappingOperator | null {
    return AvailableMappingTypes.find(
      (mt) => mt.type === this.mappingTypeControl?.value,
    ) as MappingOperator;
  }

  get showAddCondition(): boolean {
    return (
      this.allowCondition &&
      (!this.conditionalStatementListControl?.value ||
        !this.conditionalStatementListControl?.value?.conditionalStatements
          ?.length)
    );
  }

  get showActionsMenu(): boolean {
    return (
      this.showAddCondition || this.allowClearAllConditions || this.allowDelete
    );
  }

  get showAdditionalProcessor(): boolean {
    return (
      this.additionalProcessorFormGroup &&
      Object.keys(this.additionalProcessorFormGroup.controls).length > 0
    );
  }
  //#endregion

  ngOnInit(): void {
    this.mappingRuleFormGroup = this._controlContainer
      .control as UntypedFormGroup;

    this.mappingTypeControl.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((newType) => {
        this.mappingValueControl.setValue(null);
        this._checkRequiredValue(newType, true);
      });
  }

  addCondition() {
    if (this.mappingRuleFormGroup) {
      this._fb.addNewConditionalStatementList(this.mappingRuleFormGroup);
      this._cd.detectChanges();
    }

    // needed to fix nested actions menus stuck on UI, (to reproduce click menu -> add conditions)
    this._cd.detectChanges();
  }

  clearAllConditions() {
    if (this.mappingRuleFormGroup) {
      this._fb.clearAllConditions(this.mappingRuleFormGroup);
    }
  }

  addFindReplace() {
    const exist = this.additionalProcessorFormGroup.controls.find_replace;

    if (!exist) {
      this.additionalProcessorFormGroup.addControl(
        'find_replace',
        new FormControl({}),
      );
    }
  }

  private _checkRequiredValue(
    operatorType: MappingRuleType,
    emitEvent: boolean,
  ) {
    switch (operatorType) {
      case 'leave_empty':
        this.mappingValueControl.setValidators(null);
        this.mappingValueControl?.updateValueAndValidity({emitEvent});
        break;

      default:
        this.mappingValueControl.setValidators([Validators.required]);
        this.mappingValueControl?.updateValueAndValidity({emitEvent});
        break;
    }
  }
}
